import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { programs } from '@/mocks/neurobaby';

const regressions = [
  {
    id: 1,
    title: 'Regresión de los 4 meses',
    icon: 'ri-refresh-line',
    brain: 'El cerebro de tu bebé está reorganizando su arquitectura del sueño. Hasta los 4 meses, el sueño es dominado por la fase REM (activa). Pero ahora, el sistema nervioso central comienza a desarrollar los ciclos de sueño de adulto: alternancia entre sueño ligero (N1, N2), profundo (N3) y REM. Es como si el cerebro estuviera "cambiando de sistema operativo" y todavía no sabe cómo conectar las fases sin despertarse. La amígdala —el centro de alarma del cerebro— se activa con cada transición de fase, porque el bebé no ha aprendido a "pasar" de una fase a otra sin despertarse.',
    duration: '2 a 6 semanas. En algunos bebés, hasta 8 semanas.',
    howTo: 'No intentés "entrenar" al sueño en este momento. Su cerebro está en plena reorganización. Enfocate en la consistencia de la rutina, no en el resultado. Ofrecé contacto piel con piel más veces de lo habitual. El bebé necesita sentir tu presencia física para calmar la amígdala. Aceptá que los despertares serán más frecuentes — no es un retroceso, es una reconstrucción.',
  },
  {
    id: 2,
    title: 'Regresión de los 8-10 meses',
    icon: 'ri-brain-line',
    brain: 'Explosión cognitiva. Tu bebé está aprendiendo a sentarse solo, a gatear, a entender que los objetos siguen existiendo aunque no los vea (permanencia del objeto). El hipocampo —la estructura que codifica la memoria— está trabajando en sobrecarga. Durante el sueño, el cerebro consolida todo lo aprendido durante el día. Pero como hay tanto para consolidar, las transiciones entre fases de sueño se vuelven turbulentas. El bebé se despierta "practicando" gatear en la cuna, o sentándose, porque el cerebro no sabe apagar el modo "aprendizaje".',
    duration: '3 a 6 semanas. Suele ser la más intensa emocionalmente.',
    howTo: 'Dale más tiempo de juego libre durante el día para que el cerebro "descargue" antes de dormir. La rutina de sueño necesita ser más larga y más calmada: 30-45 minutos en lugar de 15. Tu bebé necesita ayuda para "apagar" el modo exploración. Si se despierta practicando gatear, no lo levantés de la cuna. Sentate al lado, tocálo suavemente y decile "estás seguro, podés dormir". Su cerebro necesita tu voz como ancla.',
  },
  {
    id: 3,
    title: 'Regresión de los 12 meses',
    icon: 'ri-walk-line',
    brain: 'Primeros pasos, nuevo mundo. La corteza motora primaria está en plena reorganización: las neuronas están creando nuevas conexiones sinápticas para la marcha, el equilibrio y la coordinación. El cerebelo —el centro del equilibrio— está en modo de desarrollo acelerado. Además, el bebé empieza a entender que es un ser separado de vos. Esa conciencia genera ansiedad de separación, porque su cerebro ahora SABE que podés irte y no volver. El sueño se desorganiza porque el sistema de alarma (amígdala) se activa con cada separación, incluso durante la noche.',
    duration: '2 a 4 semanas. A veces coincide con el destete.',
    howTo: 'Antes de la rutina de sueño, hacé "juego de despedida" durante el día: salí de la habitación 30 segundos, volvé, y celebrá la reunión. Eso le enseña a su cerebro que la separación es temporal. Durante la noche, si llora, respondé rápido. En esta etapa, el "esperar" no funciona porque la ansiedad de separación es real y neurobiológica. Tu presencia rápida le enseña que el mundo es seguro aunque vos te vayas.',
  },
  {
    id: 4,
    title: 'Regresión de los 18 meses',
    icon: 'ri-speak-line',
    brain: 'Lenguaje, autonomía, miedo a la separación. La explosión del lenguaje —que empieza entre los 18 y 24 meses— reorganiza el lóbulo temporal izquierdo y las áreas de Broca y Wernicke. El bebé está aprendiendo que las palabras tienen poder, que puede pedir cosas, que puede negarse. Esa autonomía genera conflicto interno: quiere ser independiente, pero también necesita sentirse seguro. El sueño se desorganiza porque el cerebro está procesando la identidad: "¿soy un bebé o un niño?". El miedo a la separación se intensifica porque ahora entiende las palabras "me voy" y "adiós".',
    duration: '2 a 6 semanas. Puede ser intermitente.',
    howTo: 'Dale opciones durante el día: "¿querés la camiseta azul o la roja?". Eso reduce la frustración de la autonomía. En la rutina de sueño, leéle el mismo libro todas las noches. La predictibilidad calma la amígdala cuando el cerebro está en caos de desarrollo. Si se despierta y llama, respondé con palabras: "estoy acá, estás seguro". El lenguaje ahora es una herramienta de regulación porque su cerebro entiende lo que decís.',
  },
];

const tools = [
  {
    id: 1,
    title: 'Volver a los básicos',
    icon: 'ri-restart-line',
    description: 'En medio de una regresión, todo lo que aprendiste parece no funcionar. Y eso está bien. El cerebro de tu bebé está en otra etapa. Volver a los básicos no es retroceder: es adaptarse. Rutina simple, contacto físico, presencia calmada. No hace falta inventar nada nuevo. El bebé necesita sentir que, aunque su cerebro esté cambiando, su mundo externo sigue siendo predecible.',
  },
  {
    id: 2,
    title: 'Regularte vos primero',
    icon: 'ri-heart-pulse-line',
    description: 'Tu sistema nervioso es el mapa de tu bebé. Cuando vos estás en pánico, frustrada o agotada, tu bebé siente tu frecuencia cardíaca, tu respiración acelerada, tu tensión muscular. Y su cerebro interpreta eso como peligro. No podés calmarte a vos misma con la misma facilidad que calmás a tu bebé, pero podés intentar: una respiración antes de entrar a la habitación, un vaso de agua, un minuto de silencio. No es egoísmo. Es neurobiología.',
  },
  {
    id: 3,
    title: 'Recordar que es temporal',
    icon: 'ri-time-line',
    description: 'Todas las regresiones tienen un fin. El cerebro del bebé necesita tiempo para consolidar las nuevas conexiones, para integrar las nuevas habilidades. Una vez que el sistema nervioso "se acostumbra" a la nueva arquitectura cerebral, el sueño vuelve a estabilizarse. No sabemos exactamente cuándo, pero sabemos que sí. Cada bebé que pasó por una regresión, salió de ella. Y el tuyo también va a salir.',
  },
];

export default function RegresionesSueno() {
  const [activeRegression, setActiveRegression] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const activeRegressionData = regressions[activeRegression];

  return (
    <div className="min-h-screen bg-cream">
      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-sand/40">
        <div className="h-full bg-terracotta transition-all duration-150" style={{ width: `${scrollProgress}%` }} />
      </div>

      <Navbar />

      {/* Hero */}
      <section className="relative pt-20 md:pt-28 pb-8 md:pb-14 px-6 md:px-10">
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-center mb-6 md:mb-10">
            <div className="w-full lg:w-1/2">
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-block bg-[#AAB7A3] text-white text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('regresiones.tag1')}
                </span>
                <span className="inline-block bg-terracotta text-white text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('regresiones.tag2')}
                </span>
                <span className="inline-block bg-sand text-[#3D2B1F] text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('common.basado_neurociencia')}
                </span>
              </div>
              <h1 className="font-serif text-xl md:text-2xl lg:text-3xl font-bold text-[#8B6F5C] leading-tight mb-4">
                {t('regresiones.title1')} {t('regresiones.title2')}
              </h1>
              <p className="font-sans text-sm md:text-base text-[#3D2B1F] leading-relaxed">
                {t('regresiones.hero.desc')}
              </p>
            </div>
            <div className="w-full lg:w-1/2 flex items-center justify-center">
              <img
                src="https://storage.helloreaddy.io/project_files/33950b86-7657-493b-8177-e5932d2de9a1/27430e21-aba6-4d9b-aa77-2157f795aad0_compressed_ChatGPT-Image-11-ago-2026-10_04_41-p.m..webp"
                alt="Mercedes — NeuroBabys"
                className="w-full max-w-[270px] md:max-w-[336px] h-auto rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Introducción Emocional */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-white">
        <div className="w-full max-w-3xl mx-auto">
          <div className="bg-cream rounded-3xl p-8 md:p-12 border-l-4 border-terracotta">
            <p className="font-sans text-lg md:text-xl text-[#3D2B1F] leading-relaxed mb-6">
              Finalmente habías logrado que tu bebé durmiera. Las noches eran predecibles. Te sentías segura. Y de repente, todo volvió atrás.
            </p>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
              Sentís que fracasaste. Que todo lo que hiciste no sirvió. Que estás más agotada que antes porque ahora sabés lo que es dormir, y volviste a perderlo.
            </p>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
              <strong>No fracasaste.</strong>
            </p>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
              Lo que estás viviendo tiene nombre, tiene ciencia, tiene explicación.
            </p>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed">
              Y sobre todo, tiene fin.
            </p>
          </div>
        </div>
      </section>

      {/* Frase poética */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-3xl mx-auto text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-6">
            <i className="ri-moon-line text-cream text-2xl" />
          </div>
          <p className="font-serif text-xl md:text-2xl text-white leading-relaxed mb-6">
            "Una regresión no es tu bebé rompiéndose. Es tu bebé expandiéndose.
          </p>
          <p className="font-serif text-xl md:text-2xl text-white leading-relaxed mb-6">
            Su cerebro está haciendo conexiones tan nuevas y tan grandes que el sueño se desorganiza.
          </p>
          <p className="font-serif text-xl md:text-2xl text-white leading-relaxed mb-6">
            Está creciendo.
          </p>
          <p className="font-serif text-xl md:text-2xl text-white leading-relaxed">
            Y vos estás ahí para sostenerlo mientras lo hace."
          </p>
        </div>
      </section>

      {/* Las 4 Regresiones con Tabs */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
              {t('regresiones.tabs_tag')}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              {t('regresiones.tabs_title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F] max-w-2xl mx-auto">
              {t('regresiones.tabs_desc')}
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 mb-8 md:mb-12 justify-center">
            {regressions.map((regression, index) => (
              <button
                key={regression.id}
                onClick={() => setActiveRegression(index)}
                className={`inline-flex items-center gap-2 font-sans text-sm font-semibold px-5 py-3 rounded-full transition-all duration-300 whitespace-nowrap ${
                  activeRegression === index
                    ? 'bg-terracotta text-white shadow-sm'
                    : 'bg-white text-[#3D2B1F] hover:bg-sand/50 border border-sand/40'
                }`}
              >
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-white/20 text-xs font-bold">
                  {regression.id}
                </span>
                {regression.title}
              </button>
            ))}
          </div>

          {/* Active Regression Content */}
          <div className="bg-white rounded-3xl p-6 md:p-10 border border-sand/40">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#AAB7A3]/15 text-[#AAB7A3]">
                <i className={`${activeRegressionData.icon} text-xl`} />
              </div>
              <div>
                <span className="font-sans text-xs font-semibold text-terracotta uppercase tracking-wider">
                  Regresión {activeRegressionData.id} de 4
                </span>
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#8B6F5C] leading-tight">
                  {activeRegressionData.title}
                </h3>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8">
              <div className="bg-cream rounded-2xl p-6 h-full">
                <span className="inline-block font-sans text-xs font-semibold text-[#5C4A3A] uppercase tracking-wider mb-3">
                  {t('regresiones.que_pasa')}
                </span>
                <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed">
                  {activeRegressionData.brain}
                </p>
              </div>
              <div className="bg-terracotta/10 rounded-2xl p-6 h-full border border-terracotta/20">
                <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-wider mb-3">
                  {t('regresiones.cuanto_dura')}
                </span>
                <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed font-medium">
                  {activeRegressionData.duration}
                </p>
              </div>
              <div className="bg-cream rounded-2xl p-6 h-full">
                <span className="inline-block font-sans text-xs font-semibold text-[#5C4A3A] uppercase tracking-wider mb-3">
                  {t('regresiones.como_acompanar')}
                </span>
                <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed">
                  {activeRegressionData.howTo}
                </p>
              </div>
            </div>

            {/* Navigation arrows */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-sand/30">
              <button
                onClick={() => setActiveRegression((prev) => (prev > 0 ? prev - 1 : regressions.length - 1))}
                className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-[#3D2B1F] hover:text-terracotta transition-colors duration-300"
              >
                <i className="ri-arrow-left-line" />
                {t('regresiones.regresion_anterior')}
              </button>
              <span className="font-sans text-xs text-[#5C4A3A]">
                {activeRegression + 1} / {regressions.length}
              </span>
              <button
                onClick={() => setActiveRegression((prev) => (prev < regressions.length - 1 ? prev + 1 : 0))}
                className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-[#3D2B1F] hover:text-terracotta transition-colors duration-300"
              >
                {t('regresiones.regresion_siguiente')}
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Herramientas Concretas */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-white">
        <div className="w-full max-w-4xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
              {t('regresiones.herramientas_tag')}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              {t('regresiones.herramientas_title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F] max-w-2xl mx-auto">
              No necesitás más información. Necesitás menos, pero más profundo. Estas tres herramientas funcionan en todas las regresiones.
            </p>
          </div>

          <div className="space-y-6 md:space-y-8">
            {tools.map((tool) => (
              <div key={tool.id} className="bg-cream rounded-3xl p-6 md:p-10 border border-sand/40">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#AAB7A3]/15 text-[#AAB7A3] flex-shrink-0">
                    <i className={`${tool.icon} text-2xl`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-terracotta text-white font-sans text-xs font-bold">
                        {tool.id}
                      </span>
                      <h3 className="font-serif text-xl md:text-2xl font-bold text-[#8B6F5C] leading-tight">
                        {tool.title}
                      </h3>
                    </div>
                    <p className="font-sans text-base text-[#3D2B1F] leading-relaxed">
                      {tool.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cierre Emocional */}
      <section className="py-4 md:py-6 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-3xl mx-auto text-left">
          <div className="w-10 h-10 flex items-center justify-center mb-4">
            <i className="ri-heart-3-line text-cream text-2xl" />
          </div>
          <p className="font-serif text-base md:text-lg text-white leading-relaxed mb-2">
            "El agotamiento que sentís no es debilidad. Es el precio del amor más grande que existe.
          </p>
          <p className="font-serif text-base md:text-lg text-white leading-relaxed mb-2">
            Estás sosteniendo a un ser humano que está aprendiendo a existir en el mundo.
          </p>
          <p className="font-serif text-base md:text-lg text-white leading-relaxed mb-2">
            Eso es lo más difícil y lo más sagrado que hay.
          </p>
          <p className="font-serif text-base md:text-lg text-white leading-relaxed mb-2">
            Y lo estás haciendo sola, en la oscuridad, sin que nadie te aplauda.
          </p>
          <p className="font-serif text-base md:text-lg text-white leading-relaxed">
            Pero tu bebé lo sabe. Lo siente en cada célula. Vos sos su mundo entero. Y ese mundo, hoy, le está enseñando que las tormentas pasan."
          </p>
        </div>
      </section>

      {/* CTA al Programa Completo */}
      <section className="py-10 md:py-16 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-2">
              {t('tutorial.next_step')}
            </span>
            <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#8B6F5C] leading-tight mb-3">
              Esto es solo el principio
            </h2>
            <p className="font-sans text-sm md:text-base text-[#3D2B1F] max-w-2xl mx-auto">
              El Programa Completo NeuroBabys incluye todo lo que necesitás para transformar el sueño de tu familia, paso a paso, con neurociencia y yoga mamá-bebé.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {programs.map((program) => (
              <div
                key={program.id}
                className="group bg-white rounded-2xl overflow-hidden border border-sand/40 transition-all duration-300 hover:border-terracotta/40 flex flex-col"
              >
                <div className="h-40 overflow-hidden relative">
                  <img
                    src={program.image}
                    alt={t(`programs.items.${program.id}.title`, program.title)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="font-sans text-xs font-semibold text-[#8B6F5C]">
                      {t(`programs.items.${program.id}.level`, program.level)}
                    </span>
                  </div>
                  {'badge' in program && (program as any).badge && (
                    <div className="absolute top-3 right-3 bg-terracotta text-white px-3 py-1 rounded-full">
                      <span className="font-sans text-xs font-semibold">{(program as any).badge}</span>
                    </div>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-grow">
                  <span className="font-sans text-xs text-[#5C4A3A] mb-1">
                    {t(`programs.items.${program.id}.subtitle`, program.subtitle)}
                  </span>
                  <h3 className="font-serif text-lg md:text-xl font-bold text-[#8B6F5C] mb-2 leading-tight">
                    {t(`programs.items.${program.id}.title`, program.title)}
                  </h3>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed mb-4 flex-grow">
                    {t(`programs.items.${program.id}.description`, program.description)}
                  </p>
                  <div className="flex items-center gap-1.5 mb-4 text-xs text-[#5C4A3A]">
                    <i className="ri-book-open-line text-xs" />
                    <span>{program.lessons} {t('common.guias')}</span>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-sand/30">
                    <div>
                      <span className="font-sans text-2xl font-bold text-[#8B6F5C]">${program.price}</span>
                      <span className="font-sans text-xs text-[#5C4A3A] ml-1">{t('common.usd')}</span>
                    </div>
                    <Link
                      to={program.id === 1 ? '/fundamentos-sueno' : program.id === 2 ? '/yoga-sueno-profundo' : '/programa-completo-neurobabys'}
                      className="inline-flex items-center bg-terracotta text-white px-5 py-2.5 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap"
                    >
                      {t('common.conocer_mas')}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}